import { useEffect } from 'react'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/authStore'

async function loadParticipant(userId: string, setParticipant: (p: any) => void) {
  // Primary: match by auth_user_id (reliable, set during onboarding)
  const { data: byAuthId } = await supabase
    .from('participants')
    .select('*')
    .eq('auth_user_id', userId)
    .maybeSingle()

  if (byAuthId) { setParticipant(byAuthId); return }

  // Fallback: check user metadata for participant_id (legacy sessions)
  const { data: { user } } = await supabase.auth.getUser()
  const participantId = user?.user_metadata?.participant_id

  if (participantId) {
    const { data } = await supabase
      .from('participants')
      .select('*')
      .eq('id', participantId)
      .single()
    if (data) {
      // Backfill auth_user_id so next load uses the fast path
      await supabase
        .from('participants')
        .update({ auth_user_id: userId })
        .eq('id', participantId)
      setParticipant(data)
      return
    }
  }
}

export function useAuthInit() {
  const { setUser, setParticipant, setLoading, setInit } = useAuthStore()

  useEffect(() => {
    async function init() {
      // Check for Supabase auth session first
      const { data: { session } } = await supabase.auth.getSession()

      if (session?.user) {
        setUser({ id: session.user.id, email: session.user.email })
        await loadParticipant(session.user.id, setParticipant)
      } else {
        setUser(null)
        // Check for guest participant in localStorage
        const guestId = localStorage.getItem('room_participant_id')
        if (guestId) {
          const { data } = await supabase
            .from('participants')
            .select('*')
            .eq('id', guestId)
            .maybeSingle()
          if (data) setParticipant(data)
        }
      }

      setLoading(false)
      setInit(true)
    }

    init()

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        setUser({ id: session.user.id, email: session.user.email })
        loadParticipant(session.user.id, setParticipant)
      } else {
        setUser(null)
      }
      setLoading(false)
    })

    return () => subscription.unsubscribe()
  }, [setUser, setParticipant, setLoading, setInit])
}

export function useAuth() {
  return useAuthStore()
}
